import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Calendar, MapPin, ExternalLink, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { title: "", description: "", event_date: "", time: "", location: "", category: "Community", rsvp_link: "" };

export default function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // event object or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/events`);
      setEvents(data);
    } catch (e) {
      toast.error("Could not load events");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openNew() {
    setEditing(null);
    setForm(empty);
    setShowForm(true);
  }
  function openEdit(ev) {
    setEditing(ev);
    setForm({
      title: ev.title || "",
      description: ev.description || "",
      event_date: ev.event_date || "",
      time: ev.time || "",
      location: ev.location || "",
      category: ev.category || "Community",
      rsvp_link: ev.rsvp_link || "",
    });
    setShowForm(true);
  }
  function closeForm() {
    setShowForm(false);
    setEditing(null);
    setForm(empty);
  }

  async function save(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.event_date || !form.location.trim()) {
      toast.error("Title, description, date and location are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/events/${editing.id}`, form, { withCredentials: true });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, form, { withCredentials: true });
        toast.success("Event created");
      }
      closeForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save event");
    } finally {
      setSaving(false);
    }
  }

  async function remove(ev) {
    if (!window.confirm(`Delete event "${ev.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/events/${ev.id}`, { withCredentials: true });
      toast.success("Event deleted");
      await load();
    } catch (e) {
      toast.error("Could not delete event");
    }
  }

  return (
    <div className="space-y-6" data-testid="events-admin">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-[#1F2A2E] mb-1">Events</h1>
          <p className="text-sm text-[#6B7A80]">Manage the events that appear on the public /events page.</p>
        </div>
        <button className="btn-primary" onClick={openNew} data-testid="events-new-btn">
          <Plus className="w-4 h-4" /> New event
        </button>
      </div>

      {loading ? (
        <div className="text-[#6B7A80]">Loading...</div>
      ) : events.length === 0 ? (
        <div className="bg-white border border-[#F0E5DE] rounded-2xl p-10 text-center">
          <Calendar className="w-10 h-10 text-[#E2718A] mx-auto mb-3" />
          <div className="font-display text-2xl text-[#1F2A2E] mb-2">No events yet</div>
          <p className="text-[#6B7A80] mb-5">Add your first event so it appears on navicasupports.com/events.</p>
          <button className="btn-primary" onClick={openNew}>
            <Plus className="w-4 h-4" /> Create first event
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {events.map((ev) => (
            <div key={ev.id} className="bg-white border border-[#F0E5DE] rounded-2xl p-5 lg:p-6 flex flex-wrap gap-4 items-start" data-testid={`event-row-${ev.id}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-[#FCE7EB] text-[#E2718A]">
                    {ev.category || "Community"}
                  </span>
                  <span className="text-xs text-[#6B7A80] flex items-center gap-1"><Calendar className="w-3 h-3" /> {ev.event_date}{ev.time ? ` · ${ev.time}` : ""}</span>
                </div>
                <h3 className="font-display text-2xl text-[#1F2A2E] leading-tight mb-2">{ev.title}</h3>
                <p className="text-sm text-[#4A595E] leading-relaxed mb-3">{ev.description}</p>
                <div className="flex flex-wrap gap-4 text-xs text-[#6B7A80]">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {ev.location}</span>
                  {ev.rsvp_link && (
                    <a href={ev.rsvp_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#E2718A] hover:underline">
                      <ExternalLink className="w-3.5 h-3.5" /> RSVP link
                    </a>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button className="btn-outline !px-4 !py-2 text-sm" onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`}>
                  <Edit3 className="w-3.5 h-3.5" /> Edit
                </button>
                <button className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-red-200 text-red-600 text-sm hover:bg-red-50 transition-colors" onClick={() => remove(ev)} data-testid={`event-delete-${ev.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeForm}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 lg:p-8 space-y-4" data-testid="events-form">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-[#1F2A2E]">{editing ? "Edit event" : "New event"}</h2>
              <button type="button" onClick={closeForm} className="text-[#6B7A80] hover:text-[#1F2A2E]"><X className="w-5 h-5" /></button>
            </div>
            <Field label="Title" required>
              <input className="input-navica" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="events-form-title" />
            </Field>
            <Field label="Description" required>
              <textarea rows={4} className="input-navica" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="events-form-desc" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Date (YYYY-MM-DD)" required>
                <input type="date" className="input-navica" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} required data-testid="events-form-date" />
              </Field>
              <Field label="Time">
                <input className="input-navica" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="e.g. 10:00am - 12:00pm" data-testid="events-form-time" />
              </Field>
            </div>
            <Field label="Location" required>
              <input className="input-navica" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required placeholder="Venue, suburb, address" data-testid="events-form-location" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Category">
                <select className="input-navica" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="events-form-category">
                  <option>Community</option>
                  <option>NDIS Info</option>
                  <option>Awareness</option>
                  <option>Mind & Motion</option>
                  <option>Workshop</option>
                </select>
              </Field>
              <Field label="RSVP link (optional)">
                <input className="input-navica" value={form.rsvp_link} onChange={(e) => setForm({ ...form, rsvp_link: e.target.value })} placeholder="https:// or mailto:" data-testid="events-form-rsvp" />
              </Field>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="btn-primary" disabled={saving} data-testid="events-form-submit">
                {saving ? "Saving..." : editing ? "Save changes" : "Create event"}
              </button>
              <button type="button" className="btn-outline" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">
        {label} {required && <span className="text-[#E2718A]">*</span>}
      </label>
      {children}
    </div>
  );
}
